/**
 * Procedural drystone wall texture (design:
 * docs/plans/2026-07-02-drystone-wall-appearance-design.md). Purely cosmetic —
 * NO bearing on any LOS result (hard constraint #1). Mirrors facadeTexture.ts:
 * pure three-free layout maths unit-tested in Node; canvas painting is a thin
 * wrapper exercised in e2e (happy-dom has no 2D context, so painters must
 * no-op to a flat/transparent fill rather than crash under test).
 *
 * Callers must reuse a small fixed set of variant seeds and share the cached
 * textures across every wall/road — never build one texture per wall.
 */
import { CanvasTexture, RepeatWrapping, SRGBColorSpace } from 'three';

/** Physical size of one body texture tile (metres). Wall UVs are in metres,
 * so one tile covers 4 m of wall run x 1.5 m of height (>= WALL_MAX_H_M). */
export const DRYSTONE_TEX_W_M = 4;
export const DRYSTONE_TEX_H_M = 1.5;

/** Deterministic fractional hash in [0, 1) (same idiom as turbines.ts). */
function hash01(i: number): number {
  const x = Math.sin(i * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

export interface Stone {
  x: number; // metres from the left edge of the row
  w: number;
}
export interface Course {
  y: number; // metres from the bottom of the tile
  h: number;
  stones: Stone[];
}

/** Irregular stone courses filling the body tile exactly (so RepeatWrapping
 * tiles seamlessly): row heights ~0.12-0.4 m, stone widths ~0.14-0.735 m, both
 * jittered per seed; the last row/stone absorbs the remainder. */
export function stoneCourses(seed: number): Course[] {
  const courses: Course[] = [];
  let y = 0;
  let row = 0;
  while (y < DRYSTONE_TEX_H_M - 0.12) {
    let h = 0.16 + hash01(seed * 31 + row * 7.3) * 0.12;
    if (DRYSTONE_TEX_H_M - (y + h) < 0.12) h = DRYSTONE_TEX_H_M - y; // absorb remainder
    const stones: Stone[] = [];
    let x = 0;
    let i = 0;
    while (x < DRYSTONE_TEX_W_M - 0.14) {
      let w = 0.18 + hash01(seed * 131 + row * 17.7 + i * 3.1) * 0.42;
      if (DRYSTONE_TEX_W_M - (x + w) < 0.14) w = DRYSTONE_TEX_W_M - x;
      stones.push({ x, w });
      x += w;
      i += 1;
    }
    courses.push({ y, h, stones });
    y += h;
    row += 1;
  }
  return courses;
}

const PX_PER_M = 64;

const bodyCache = new Map<number, CanvasTexture>();

/** Metres above the wall base the moss band stays within. Wall UVs run
 * v = 0 (base) .. wallHeight (roadWalls.ts), and every wall is at least
 * WALL_MIN_H_M (0.9 m) tall, so a band this close to the base is guaranteed
 * to be sampled on every wall in the height range — not just the 1.5 m
 * maximum (issue #37: it used to sit at the top of the tile, a region only
 * the tallest walls' UVs ever reach). */
const MOSS_BAND_H_M = 0.12;

export interface MossSpot {
  x: number; // fraction of the tile width, [0, 1)
  yFromBaseM: number; // metres above the wall base, within [0, MOSS_BAND_H_M)
}

/** Deterministic moss-blotch placement near the wall base (see MOSS_BAND_H_M). */
export function mossBand(seed: number, count: number): MossSpot[] {
  const spots: MossSpot[] = [];
  for (let i = 0; i < count; i++) {
    spots.push({
      x: hash01(seed * 41 + i * 6.7),
      yFromBaseM: hash01(i * 3.3) * MOSS_BAND_H_M,
    });
  }
  return spots;
}

function stoneFill(seed: number): string {
  // weathered grey-brown with a warm sandstone push; darker than a fresh
  // quarry face so the dark joints stay dominant (reference photo)
  const l = 38 + hash01(seed * 3.7) * 14; // lightness 38-52%
  const hue = 28 + hash01(seed * 9.1) * 14;
  const sat = 5 + hash01(seed * 5.3) * 9;
  return `hsl(${hue.toFixed(0)}, ${sat.toFixed(0)}%, ${l.toFixed(0)}%)`;
}

/** White/pale lichen blotches, the strongest cue in the reference photo. */
function paintLichen(ctx: CanvasRenderingContext2D, seed: number, w: number, h: number, count: number): void {
  for (let i = 0; i < count; i++) {
    const x = hash01(seed * 13 + i * 7.1) * w;
    const y = hash01(seed * 17 + i * 3.9) * h;
    const r = (2 + hash01(seed * 23 + i * 5.3) * 9) * (PX_PER_M / 64);
    ctx.fillStyle = `rgba(210, 213, 200, ${0.25 + hash01(seed * 29 + i) * 0.3})`;
    ctx.beginPath();
    ctx.ellipse(x, y, r, r * (0.6 + hash01(i + seed) * 0.5), hash01(i * 2.2) * Math.PI, 0, Math.PI * 2);
    ctx.fill();
  }
}

/** Builds (or returns the cached) drystone body texture for a variant seed. */
export function makeDrystoneTexture(variantSeed: number): CanvasTexture {
  const cached = bodyCache.get(variantSeed);
  if (cached) return cached;
  const canvas = document.createElement('canvas');
  canvas.width = DRYSTONE_TEX_W_M * PX_PER_M;
  canvas.height = DRYSTONE_TEX_H_M * PX_PER_M;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    // deep recessed gaps show wherever stones don't cover; darker than the
    // stone so joints read as shadowed crevices rather than flat mortar
    ctx.fillStyle = '#1e1a15';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const toPx = (m: number) => m * PX_PER_M;
    for (const [r, row] of stoneCourses(variantSeed).entries()) {
      for (const [i, s] of row.stones.entries()) {
        const sSeed = variantSeed * 101 + r * 31 + i;
        const cellW = toPx(s.w);
        const cellH = toPx(row.h);
        // per-stone, per-side joint inset: some hairline, some wide dark
        // crevices, so stones read as individual lumps not coursed brick.
        // Capped to a quarter of the cell so small stones never invert, and
        // always inside the cell so the tile still wraps seamlessly.
        const gapMax = Math.min(5, cellW * 0.25, cellH * 0.25);
        const gapL = 1 + hash01(sSeed * 2.3) * (gapMax - 1);
        const gapR = 1 + hash01(sSeed * 4.1) * (gapMax - 1);
        const gapT = 1 + hash01(sSeed * 6.7) * (gapMax - 1);
        const gapB = 1 + hash01(sSeed * 8.9) * (gapMax - 1);
        let px = toPx(s.x) + gapL;
        // canvas y runs down; course y runs up from the wall base
        let py = canvas.height - toPx(row.y + row.h) + gapT;
        let pw = cellW - gapL - gapR;
        let ph = cellH - gapT - gapB;
        // ~7% of stones sit as a shrunken lump offset within their cell, opening
        // a chunky dark void beside them — a missing / slipped stone
        if (hash01(sSeed * 12.1) < 0.07) {
          const shrink = 0.55 + hash01(sSeed * 3.3) * 0.2;
          const nw = pw * shrink;
          const nh = ph * shrink;
          px += hash01(sSeed * 5.9) * (pw - nw);
          py += hash01(sSeed * 7.1) * (ph - nh);
          pw = nw;
          ph = nh;
        }
        ctx.fillStyle = stoneFill(sSeed);
        ctx.beginPath();
        ctx.roundRect(px, py, pw, ph, Math.min(3, pw / 2, ph / 2));
        ctx.fill();
        // nudge the top edge up a few px into the gap above so the courses read
        // as uneven drystone rather than ruler-straight brick lines
        const topRise = 2 + hash01(sSeed * 1.7) * 1.5;
        ctx.fillRect(px, py - topRise, pw, topRise + 1);
        // light top edge / dark bottom edge fakes relief without a normal map
        ctx.fillStyle = 'rgba(255,255,255,0.14)';
        ctx.fillRect(px, py, pw, 2);
        ctx.fillStyle = 'rgba(0,0,0,0.22)';
        ctx.fillRect(px, py + ph - 2, pw, 2);
      }
    }
    // near-black crevice shadows along the horizontal joints, so the gaps read
    // as depth rather than flat colour
    ctx.fillStyle = 'rgba(0,0,0,0.4)';
    for (const [r, row] of stoneCourses(variantSeed).entries()) {
      const jy = canvas.height - toPx(row.y); // bottom joint line of the course
      for (let k = 0; k < 3; k++) {
        const bx = hash01(variantSeed * 71 + r * 13 + k * 5.1) * canvas.width;
        const bw = toPx(0.08 + hash01(variantSeed * 3.7 + r * 2 + k) * 0.2);
        ctx.beginPath();
        ctx.ellipse(bx, jy, bw, 1.5 + hash01(r * 2.2 + k) * 2, 0, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    paintLichen(ctx, variantSeed, canvas.width, canvas.height, 45);
    // moss clings near the wall base — the one band every wall samples
    // regardless of its actual height (see MOSS_BAND_H_M)
    for (const [i, spot] of mossBand(variantSeed, 16).entries()) {
      const x = spot.x * canvas.width;
      const y = canvas.height - toPx(spot.yFromBaseM);
      ctx.fillStyle = `rgba(106, 122, 58, ${0.25 + hash01(variantSeed + i) * 0.3})`;
      ctx.beginPath();
      ctx.ellipse(x, y, toPx(0.08), toPx(0.05), 0, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  const texture = new CanvasTexture(canvas);
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.repeat.set(1 / DRYSTONE_TEX_W_M, 1 / DRYSTONE_TEX_H_M);
  // canvas HSL is authored in sRGB; anisotropy keeps courses crisp at grazing angles
  texture.colorSpace = SRGBColorSpace;
  texture.anisotropy = 4;
  bodyCache.set(variantSeed, texture);
  return texture;
}
